<script setup>
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from "@headlessui/vue";
import { ref, watch, computed } from "vue";

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  amlOfficerEmail: { type: String, default: null },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

const isOpen = ref(props.modelValue);
const emailInput = ref("");
const submitting = ref(false);

watch(() => props.modelValue, v => {
  isOpen.value = v;
  if (v) {
    emailInput.value = props.amlOfficerEmail || "";
    submitting.value = false;
  }
});
watch(isOpen, v => emit("update:modelValue", v));

const needsEmail = computed(() => !props.amlOfficerEmail || props.amlOfficerEmail.trim() === "");
const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.value || ""));

function handleConfirm() {
  if (needsEmail.value && !emailValid.value) return;
  submitting.value = true;
  emit("confirm", { amlOfficerEmail: needsEmail.value ? emailInput.value.trim() : null });
}

function handleCancel() {
  emit("cancel");
  isOpen.value = false;
}
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" class="relative z-50" @close="handleCancel">
      <TransitionChild
        as="template"
        enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/30" />
      </TransitionChild>

      <div class="fixed inset-0 flex items-center justify-center p-4">
        <TransitionChild
          as="template"
          enter="ease-out duration-300" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100"
          leave="ease-in duration-200" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95"
        >
          <DialogPanel class="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <DialogTitle class="text-lg font-semibold text-gray-900">
              {{ $gettext("Reconfirmer le dossier à risque élevé") }}
            </DialogTitle>
            <p class="mt-2 text-sm text-gray-600">
              {{ $gettext("Ce dossier présente un risque élevé. En confirmant, vous notifiez votre responsable AML par e-mail avec le détail des facteurs de risque.") }}
            </p>

            <div v-if="needsEmail" class="mt-4">
              <label class="block text-sm font-medium text-gray-700">
                {{ $gettext("Adresse e-mail du responsable AML de l'agence") }}
              </label>
              <p class="mt-1 text-xs text-gray-500">
                {{ $gettext("Aucune adresse e-mail du responsable AML n'est configurée pour cette organisation. Renseignez-la pour continuer ; elle sera enregistrée dans les paramètres.") }}
              </p>
              <input
                v-model="emailInput"
                type="email"
                class="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-primary"
                placeholder="aml.officer@example.com"
              />
              <p v-if="emailInput && !emailValid" class="mt-1 text-xs text-red-600">
                {{ $gettext("Email invalide") }}
              </p>
            </div>

            <div class="mt-6 flex justify-end gap-3">
              <button
                type="button"
                class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                @click="handleCancel"
              >
                {{ $gettext("Annuler") }}
              </button>
              <button
                type="button"
                :disabled="(needsEmail && !emailValid) || submitting"
                class="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
                @click="handleConfirm"
              >
                {{ $gettext("Confirmer le risque") }}
              </button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
